import { FC } from 'react'
import { find, isEmpty, isEqual, orderBy, uniqWith } from 'lodash'
import { Trans } from 'react-i18next'
import { Format } from 'services/Dates'
import { parseISO } from 'date-fns'

import { getAttributeNames, getPositionName, getProjectName, getRequestTypeName, getTagNames, hasAccessToThis } from 'services/Utilities'
import { generateDateText } from 'services/Requests'
import flagsPermissionsHandler from './flagsPermissionsHandler'
import { store } from 'store/reducers'
import { t } from 'translations'
import { getAttributesQueryCache } from 'Api'

/*
  Validates that the employee has all the required attributes.
  Add the missing attributes information to showReason if addReason is true
  */

type AttributeIssue = {
  missing: {
    attributeId: string
    rank: number
  }[]
  inferior: {
    attributeId: string
    rank: number
  }[]
}

type AttributeTypes = 'hasRestrictions' | 'missingSkills' | 'missingRequirements' | 'attributesToUpdate'

export const validateAttributes = (event: MData.Event): Record<AttributeTypes, AttributeIssue> => {
  const {
    resources: { projectsById, positionsById, usersById }
  } = store.getState() as MStore.RootState

  const data = getAttributesQueryCache()
  const attributesById = data?.attributesById ?? {}

  const attributeIssues: Record<AttributeTypes, AttributeIssue> = {
    hasRestrictions: { missing: [], inferior: [] },
    missingSkills: { missing: [], inferior: [] },
    missingRequirements: { missing: [], inferior: [] },
    attributesToUpdate: { missing: [], inferior: [] }
  }

  // If this shift has a project but the user lacks the permission to see projects
  // we won't be able to calculate the reasons for attributes since we need the project's attributes
  if (!isEmpty(event.projects) && !hasAccessToThis('projectSee')) return attributeIssues

  // Take all attributes from both the shift's and position's but if an attribute is in both take the shift's
  const position = positionsById[event.positionId] || {}
  const eventRequiredAttributes = event.requiredAttributes || []

  // Get the project from the event depending on the fact that it is an array or not
  let eventProjects = event.projects
  if (!Array.isArray(event.projects)) eventProjects = event.projects ? [event.projects] : []
  const [eventProjectId] = eventProjects || []
  const project = event.projects && projectsById[eventProjectId]

  // Take all attributes from both the project's and position's but if an attribute is in both take the attribute with the highest rank
  const allAttributes = eventRequiredAttributes.concat(position.attributes || []).concat(project?.requiredAttributes || [])

  const sortedAttributes = orderBy(allAttributes, 'rank', 'desc')

  const requiredAttributes = uniqWith(sortedAttributes, (a, b) => isEqual(a.attributeId, b.attributeId))
  const { employeeId } = event
  const employee = employeeId ? usersById[employeeId] : undefined

  for (const requiredAttribute of requiredAttributes) {
    // Retrieve the full attribute
    const fullAttribute = attributesById[requiredAttribute.attributeId]

    // Retrieve this required attribute from the user's attributes
    const userAttribute = employee?.attributes && find(employee.attributes, { attributeId: requiredAttribute.attributeId })
    const hasRank = requiredAttribute.rank != null

    // If this attribute is a restriction check if the user has that restriction
    if (fullAttribute.type === 'restriction') {
      // If this user has this restriction add it to the list
      if (userAttribute) {
        const isValidRank = userAttribute.rank >= requiredAttribute.rank
        // If this restriction has a rank, check if the user's rank meets the minimum required rank
        if (!hasRank || isValidRank) attributeIssues.hasRestrictions.missing.push({ attributeId: fullAttribute._id, rank: fullAttribute.rank })
      }
      // Proceed to the next attribute
      continue
    }

    // If the user doesn't have this required attribute add it to the missing attributes and proceed to the next required attribute
    if (!userAttribute) {
      if (fullAttribute.type === 'skill') attributeIssues.missingSkills.missing.push({ attributeId: fullAttribute._id, rank: fullAttribute.rank })
      if (fullAttribute.type === 'requirement') attributeIssues.missingRequirements.missing.push({ attributeId: fullAttribute._id, rank: fullAttribute.rank })
      attributeIssues.attributesToUpdate.missing.push(requiredAttribute)
      continue
    }

    // If this required attribute requires a minimum rank check if the user has the required rank for this attribute
    if (hasRank && userAttribute.rank < requiredAttribute.rank) {
      if (fullAttribute.type === 'skill') attributeIssues.missingSkills.inferior.push({ attributeId: fullAttribute._id, rank: fullAttribute.rank })
      if (fullAttribute.type === 'requirement') attributeIssues.missingRequirements.inferior.push({ attributeId: fullAttribute._id, rank: fullAttribute.rank })
      attributeIssues.attributesToUpdate.inferior.push(requiredAttribute)
      continue
    }
  }

  return attributeIssues
}

/*
  Format the flags to display them
*/
type UserFlagByEvent = MData.UserWithFlags['flags']['shiftFlags']['']
type Props = {
  flag: MData.UserFlags
  event: MData.Event
  numberOfHoursPerRange?: MData.UserWithFlags['numberOfHoursPerRange']
  userFlags?: UserFlagByEvent
}

export const FlagTrans: FC<Props> = ({ flag, event, numberOfHoursPerRange, userFlags }) => {
  const { reasons, ...flags } = userFlags || ({} as UserFlagByEvent)

  if (flagsPermissionsHandler(flag) === 'MISSING_PERMISSIONS') return <div>{t(`manager.schedule.EDIT_MODAL.USER_FLAGS.MISSING_PERMISSIONS`)}</div>
  switch (flag) {
    // Skills and attributes
    case 'hasRestrictions':
    case 'missingSkills':
    case 'missingRequirements': {
      const attributeIssues = validateAttributes(event)

      return (
        <>
          {/* Inferior attributes */}
          {attributeIssues[flag].inferior.length ? (
            <div>
              <Trans t={t} i18nKey={`manager.schedule.EDIT_MODAL.USER_FLAGS.${flag}_INFERIOR`}>
                <b>{{ attributeNames: getAttributeNames(attributeIssues[flag].inferior, { delimiter: ',' }) }}</b>
              </Trans>
            </div>
          ) : null}
          {/* Missing attributes */}
          {attributeIssues[flag].missing.length ? (
            <div>
              <Trans t={t} i18nKey={`manager.schedule.EDIT_MODAL.USER_FLAGS.${flag}_MISSING`}>
                <b>{{ attributeNames: getAttributeNames(attributeIssues[flag].missing, { delimiter: ',' }) }}</b>
              </Trans>
            </div>
          ) : null}
        </>
      )
    }

    case 'missingRegion':
      if (!reasons) return null
      return (
        <div>
          <Trans t={t} i18nKey="manager.schedule.EDIT_MODAL.USER_FLAGS.missingRegion">
            <b>{{ missingRegions: getTagNames(reasons.missingRegions, { delimiter: ', ' }) }}</b>
          </Trans>
        </div>
      )
    case 'vacationOverlapping': {
      if (!reasons) return null
      return (
        <div>
          {reasons.vacationInfo?.map(({ start, end, type }) => (
            <>
              <Trans t={t} i18nKey="manager.schedule.EDIT_MODAL.USER_FLAGS.vacationOverlapping">
                <b>{{ type: getRequestTypeName(type) }}</b>
                <i>{{ dateText: generateDateText(start, end) }}</i>
              </Trans>
              <br></br>
            </>
          ))}
        </div>
      )
    }
    case 'eventOverlapping':
      return (
        <>
          {reasons.shiftsOverlapping.map((overlappingEvent, index) => {
            const { start, end, positionId, projectId } = overlappingEvent
            const eventText = `${Format(start, { outFormat: 'humanDateTimeCompact' })} - ${Format(end, { outFormat: 'humanDateTimeCompact' })}`
            return (
              <div key={index}>
                <Trans t={t} i18nKey={`manager.schedule.EDIT_MODAL.USER_FLAGS.eventOverlapping${projectId ? '_PROJECT' : ''}`}>
                  <i>{{ eventText }}</i>
                  <b>{{ positionName: getPositionName(positionId) }}</b>
                  <b>{{ projectName: getProjectName(projectId, { canBeNull: true, noValue: '' }) }}</b>
                </Trans>
              </div>
            )
          })}
        </>
      )
    case 'eventDistance':
      if (!flags.eventOverlapping) return <div>{t('manager.schedule.EDIT_MODAL.USER_FLAGS.eventDistance')}</div>
      else return null
    case 'lockedToProject':
    case 'preferredOnProject':
      return (
        <div>
          <Trans t={t} i18nKey={`manager.schedule.EDIT_MODAL.USER_FLAGS.${flag}`}>
            <b>{{ projectName: getProjectName(flags[flag][0]) }}</b>
          </Trans>
        </div>
      )
    case 'maxNumberOfHours':
      return (
        <>
          {numberOfHoursPerRange?.map(({ numberOfHours, maxHours, range: { start, end } }, index) => (
            <div key={index}>
              <Trans t={t} i18nKey="manager.schedule.EDIT_MODAL.USER_FLAGS.maxNumberOfHours">
                <b>
                  {{ numberOfHours }}
                  {{ maxHours }}
                </b>
                <i>
                  {{
                    hoursText: `${Format(parseISO(start), { outFormat: 'humanDateCompact' })} - ${Format(parseISO(end), {
                      outFormat: 'humanDateCompact'
                    })}`
                  }}
                </i>
              </Trans>
            </div>
          ))}
        </>
      )
    case 'maxConsecutiveDays':
      return (
        <div>
          <Trans t={t} i18nKey="manager.schedule.EDIT_MODAL.USER_FLAGS.maxConsecutiveDays">
            <b>
              {{ numberOfConsecutiveDays: reasons.numberOfConsecutiveDays }}
              {{ maxConsecutiveDaysNumber: reasons.maxConsecutiveDaysNumber }}
            </b>
          </Trans>
        </div>
      )
    case 'maxNumberOfHoursPerDay':
      return (
        <div>
          <Trans t={t} i18nKey="manager.schedule.EDIT_MODAL.USER_FLAGS.maxNumberOfHoursPerDay">
            <b>
              {{
                numberOfHours: parseInt(reasons.maxNumberOfHoursPerDayNumber) / 60,
                maxHours: parseInt(reasons.maxNumberOfHoursPerDayNumberPermitted) / 60
              }}
            </b>
          </Trans>
        </div>
      )
    case 'unavailableOverlap':
      return (
        <div>
          {t('manager.schedule.EDIT_MODAL.USER_FLAGS.unavailableOverlap')}{' '}
          <i>
            {(reasons.availabilities || [])
              .map(({ type, start, end }) => t(`manager.scheduleTemplates.employeeSelectionTool.${type}_RANGE`, { start, end }))
              .join(', ')}
          </i>
        </div>
      )

    // Basic rendering
    case 'missingPosition':
    case 'leaveOverlapping':
    case 'unavailableAllDay':
    case 'pendingAvailability':
    case 'wrongDepartment':
    case 'wrongTeam':
    case 'wrongProject':
    case 'maxConsecutiveTime':
    case 'missingAdjacentShiftBefore':
    case 'missingAdjacentShiftAfter':
    case 'missingAdjacentShifts':
    case 'lockedToCurrent':
    case 'preferredOnCurrent':
    case 'blockedFromProject':
    case 'employeeExpiredContract':
    case 'employeeExpiredPermit':
    case 'expiredEmployeeType':
    case 'noContract':
    case 'noPermit':
    case 'noEmployeeType':
    case 'beforeStartDate':
    case 'afterEndDate':
    case 'archivedUser':
    case 'employeeTypeInvalid':
    case 'noCurrentAvailability':
      return <div>{t(`manager.schedule.EDIT_MODAL.USER_FLAGS.${flag}`)}</div>
    default:
      return null
  }
}
